import React, { useCallback, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  BarChartOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  InboxOutlined,
  LockOutlined,
  ReadOutlined,
  ReloadOutlined,
  RightOutlined,
  RobotOutlined,
  RocketOutlined,
  StarFilled,
  ThunderboltOutlined,
  TrophyOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Layout,
  List,
  Progress,
  Result,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import AppLayout from "@/Components/Common/AppLayout";
import ProgressCircle from "@/Components/Common/ProgressCircle";
import { ScoreSvg } from "@/Components/Common/ScoreSvg";
import { LoadingContext } from "@/Context/LoadingContext";
import "./FiveSecondScore.scss";

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;
const { Panel } = Collapse;

// Enhanced Loading animation component matching ResumeUpload design
const LoadingAnimation = ({ stage }) => {
  const stages = [
    {
      icon: <UploadOutlined />,
      text: "Uploading your resume...",
      description: "Securely uploading your file",
      progress: 0
    },
    {
      icon: <FileTextOutlined />,
      text: "Reading your resume...",
      description: "Processing your document",
      progress: 25
    },
    {
      icon: <ThunderboltOutlined />,
      text: "Analyzing with AI...",
      description: "AI engine is working",
      progress: 50
    },
    {
      icon: <BulbOutlined />,
      text: "Extracting information...",
      description: "Parsing resume sections",
      progress: 75
    },
    {
      icon: <TrophyOutlined />,
      text: "Preparing interface...",
      description: "Setting up your workspace",
      progress: 100
    }
  ];

  const currentStage = stages[Math.min(stage, stages.length - 1)];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      padding: '40px 20px'
    }}>
      {/* Main loading card */}
      <Card style={{
        width: '100%',
        maxWidth: '500px',
        textAlign: 'center',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        {/* Progress header */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '32px',
            color: 'white'
          }}>
            {currentStage.icon}
          </div>
          <Title level={2} style={{ margin: 0, color: "#333", textAlign: "center" }}>
            {currentStage.text}
          </Title>
          <Text
            style={{ color: "#666", fontSize: "16px", textAlign: "center", display: "block", marginTop: "8px" }}
          >
            {currentStage.description}
          </Text>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: '32px' }}>
          <Progress
            percent={currentStage.progress}
            strokeColor={{
              "0%": "#1890ff",
              "100%": "#40a9ff",
            }}
            trailColor="#f0f2f5"
            size={[null, 8]}
            showInfo={false}
            style={{ marginBottom: '8px' }}
          />
          <Text style={{ color: '#666', fontSize: '14px', textAlign: 'center', display: 'block' }}>
            {currentStage.progress}% Complete
          </Text>
        </div>

        {/* Steps indicator */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          padding: '0 20px'
        }}>
          {stages.map((item, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                position: 'relative'
              }}
            >
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: index <= stage ? '#1890ff' : '#f0f2f5',
                color: index <= stage ? 'white' : '#bfbfbf',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                marginBottom: '8px',
                border: index === stage ? '2px solid #40a9ff' : 'none',
                transition: 'all 0.3s ease'
              }}>
                {index < stage ? <CheckCircleOutlined /> : item.icon}
              </div>
              <Text style={{
                fontSize: '10px',
                color: index <= stage ? '#333' : '#bfbfbf',
                textAlign: 'center',
                lineHeight: '1.2'
              }}>
                {item.text.split(' ')[0]}
              </Text>
            </div>
          ))}
        </div>

        {/* Animated elements */}
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" style={{ color: '#1890ff', marginBottom: '16px' }} />
          <Text style={{ color: '#999', fontSize: '14px', display: 'block' }}>
            Please wait while we process your resume... This can take up to 1 minute.
          </Text>
        </div>
      </Card>
    </div>
  );
};

// Score level badge component
const ScoreLevelBadge = ({ level, score, showScoreText = true }) => {
  const configs = {
    poor: { color: "error", icon: <CloseCircleOutlined />, text: "Needs Major Work" },
    "needs-work": { color: "warning", icon: <ExclamationCircleOutlined />, text: "Needs Improvement" },
    good: { color: "processing", icon: <CheckCircleOutlined />, text: "Good Resume" },
    excellent: { color: "success", icon: <TrophyOutlined />, text: "Outstanding Resume" },
  };

  const config = configs[level] || configs["needs-work"];

  return (
    <Badge.Ribbon text={showScoreText ? `Score: ${score}/100` : config.text} color={config.color}>
      <Card className="score-level-card">
        <Space>
          {config.icon}
          <Text strong>{config.text}</Text>
        </Space>
      </Card>
    </Badge.Ribbon>
  );
};

// Section score card component
const SectionScoreCard = ({ title, score, whatsGood, whatToImprove, suggestions, icon }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return "#52c41a";
    if (score >= 60) return "#faad14";
    return "#ff4d4f";
  };

  return (
    <Card className="section-score-card" hoverable>
      <div
        className="section-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          paddingBottom: "16px",
          borderBottom: "1px solid #f0f0f0",
          minHeight: "60px",
        }}
      >
        <Space style={{ flex: 1, marginRight: "24px" }}>
          {icon}
          <Title level={5} style={{ margin: 0 }}>
            {title}
          </Title>
        </Space>
        <Progress
          type="circle"
          percent={score}
          width={50}
          strokeColor={getScoreColor(score)}
          style={{ flexShrink: 0 }}
        />
      </div>

      <Collapse ghost expandIconPosition="end">
        <Panel header="View Details" key="1">
          <Space direction="vertical" style={{ width: "100%" }}>
            {whatsGood?.length > 0 && (
              <div>
                <Text type="success" strong>
                  What's Good:
                </Text>
                <List
                  size="small"
                  dataSource={whatsGood}
                  renderItem={(item) => (
                    <List.Item>
                      <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                      {item}
                    </List.Item>
                  )}
                />
              </div>
            )}

            {whatToImprove?.length > 0 && (
              <div>
                <Text type="warning" strong>
                  Areas to Improve:
                </Text>
                <List
                  size="small"
                  dataSource={whatToImprove}
                  renderItem={(item) => (
                    <List.Item>
                      <ExclamationCircleOutlined style={{ color: "#faad14", marginRight: 8 }} />
                      {item}
                    </List.Item>
                  )}
                />
              </div>
            )}

            {suggestions?.length > 0 && (
              <div>
                <Text type="primary" strong>
                  Suggestions:
                </Text>
                <List
                  size="small"
                  dataSource={suggestions}
                  renderItem={(item) => (
                    <List.Item>
                      <BulbOutlined style={{ color: "#1890ff", marginRight: 8 }} />
                      {item}
                    </List.Item>
                  )}
                />
              </div>
            )}
          </Space>
        </Panel>
      </Collapse>
    </Card>
  );
};

// Quick win card component
const QuickWinCard = ({ quickWin, index }) => {
  return (
    <Card
      className="quick-win-card"
      hoverable
      style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", flex: 1 }}>
        <div
          style={{
            backgroundColor: "#722ed1",
            color: "white",
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: "600",
            flexShrink: 0,
            marginTop: "2px",
          }}
        >
          {index + 1}
        </div>
        <Space direction="vertical" style={{ width: "100%", flex: 1 }}>
          <Title level={5} style={{ margin: 0 }}>
            {quickWin.title}
          </Title>
          <Paragraph style={{ margin: 0 }}>{quickWin.description}</Paragraph>
        </Space>
      </div>
    </Card>
  );
};

const FiveSecondScore = () => {
  const navigate = useNavigate();
  const fetch = useAuthenticatedFetch();
  const { startLoading, stopLoading } = useContext(LoadingContext);

  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0);
  const [scoreData, setScoreData] = useState(null);
  const [resumeId, setResumeId] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [planInfo, setPlanInfo] = useState({
    plan: "free",
    maxResumes: 3,
    currentResumeCount: 0,
    canCreateResume: true,
  });
  const [checkingPlan, setCheckingPlan] = useState(true);

  // Auth guard - redirect to login if not authenticated
  useEffect(() => {
    const authToken = localStorage.getItem("authorization");
    const userId = localStorage.getItem("userId");
    if (!authToken || !userId) {
      navigate("/app/login?returnUrl=" + encodeURIComponent(window.location.pathname));
    }
  }, [navigate]);

  // Fetch plan info to check if user can create resume
  useEffect(() => {
    const checkPlanInfo = async () => {
      try {
        setCheckingPlan(true);
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const response = await fetch.get(`/user/dashboard/${userId}`, false);
        if (response.success && response.data.planInfo) {
          setPlanInfo(response.data.planInfo);
          // If user can't create resume, redirect to dashboard
          if (!response.data.planInfo.canCreateResume) {
            message.warning("You have reached your resume limit.");
            navigate("/app/dashboard");
          }
        }
      } catch (error) {
        console.error("Error checking plan info:", error);
      } finally {
        setCheckingPlan(false);
      }
    };
    checkPlanInfo();
  }, []);

  // Check for existing resume on mount - removed localStorage dependency
  // Users must be logged in to access their resume data

  const loadExistingScore = async (resumeId) => {
    try {
      const response = await fetch.get(`/resume-score/${resumeId}`);
      if (response.data) {
        setScoreData(response.data);
        setResumeId(resumeId);
      }
    } catch (err) {
      console.error("No existing score found", err.message || err);
    }
  };

  // Simulate loading stages with 5 second intervals
  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setLoadingStage((prev) => {
          if (prev >= 4) {
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 5000); // Changed from 2000 to 5000 for 5 seconds per stage
      return () => clearInterval(interval);
    } else {
      setLoadingStage(0);
    }
  }, [loading]);

  const validFileTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  const maxFileSize = 5 * 1024 * 1024; // 5MB

  const handleDropZoneDrop = useCallback(
    async (_dropFiles, acceptedFiles) => {
      const newFile = acceptedFiles[0];

      if (!newFile) return;

      // Validate file type
      if (!validFileTypes.includes(newFile.type)) {
        setError("Please upload only PDF or DOCX files.");
        return;
      }

      // Validate file size
      if (newFile.size > maxFileSize) {
        setError("File size must not exceed 5MB.");
        return;
      }

      setFile(newFile);
      setError("");
      // Auto-start processing
      handleUploadAndScore(newFile);
    },
    [validFileTypes, maxFileSize]
  );

  const handleUploadAndScore = async (fileToUpload = file) => {
    if (!fileToUpload) return;

    // Double-check plan limit before upload
    if (!planInfo.canCreateResume) {
      setError("You have reached your resume limit. Free plan allows only 3 resumes.");
      return;
    }

    setError("");
    setLoading(true);
    setLoadingStage(0);

    try {
      // Step 1: Upload file
      const fileDataPromise = new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(fileToUpload);
      });

      const fileData = await fileDataPromise;

      // Upload file
      const uploadResponse = await fetch.post(
        "/upload-base64-file",
        {
          fileData: fileData,
          fileName: fileToUpload.name,
          fileType: fileToUpload.type,
          fileSize: fileToUpload.size,
        },
        false
      );

      if (!uploadResponse.success) {
        throw new Error("Upload failed");
      }

      // Step 2: Parse resume
      const userId = localStorage.getItem("userId");
      const parseResponse = await fetch.post(
        "/parse-resume",
        {
          fileUrl: uploadResponse.data.assetUrl,
          fileName: uploadResponse.data.assetName,
          fileSize: fileToUpload.size,
          fileType: fileToUpload.type,
          userId: userId,
          resumeSource: "five-sec-score",
        },
        false
      );

      if (!parseResponse.data?.resumeId) {
        throw new Error("Resume parsing failed");
      }

      const newResumeId = parseResponse.data.resumeId;
      setResumeId(newResumeId);

      setLoading(false);
      message.success("Resume analyzed successfully!");

      // Navigate to ResumeAnalysis page with the resumeId
      // The parsing already includes aiAnalysis data, no separate scoring needed
      navigate(`/app/resume-score/${newResumeId}`);

    } catch (err) {
      setLoading(false);
      setError(err.message || "An error occurred during analysis. Please try again.");
      // Don't show error message for insufficient credits (402) - CreditContext modal will handle it
      if (err?.response?.status !== 402 && err?.response?.data?.code !== "INSUFFICIENT_CREDITS") {
        message.error("Failed to analyze resume");
      }
    }
  };

  const handleEnhanceClick = async () => {
    if (!resumeId) return;

    // Track enhancement click
    await fetch.post("/resume-score/track-enhancement", { resumeId }, false);

    // Navigate to chat interface
    navigate(`/app/resume/${resumeId}`);
  };

  const handleTryAnother = () => {
    setFile(null);
    setScoreData(null);
    setResumeId(null);
    setError("");
    clearCurrentResumeId();
  };

  // Handle back navigation
  const handleBack = () => {
    navigate("/app/resume-choice");
  };

  // Navigation menu items
  const getNavigationItems = (scoreData) => [
    {
      key: "overview",
      icon: <TrophyOutlined />,
      label: "Score Overview",
      description: "Main score and summary",
    },
    {
      key: "sections",
      icon: <BarChartOutlined />,
      label: "Section Analysis",
      description: "Detailed breakdown by section",
    },
    {
      key: "strengths",
      icon: <CheckCircleOutlined />,
      label: "Strengths & Improvements",
      description: "What works and what needs work",
    },
    {
      key: "quickwins",
      icon: <RocketOutlined />,
      label: "Quick Wins",
      description: "Easy improvements for big impact",
      disabled: !scoreData?.quickWins?.length,
    },
    {
      key: "industry",
      icon: <BulbOutlined />,
      label: "Industry Fit",
      description: "How well you match your field",
      disabled: !scoreData?.aiAnalysis?.industryFit,
    },
  ];

  // Render active section content
  const renderActiveSection = (sectionKey, scoreData) => {
    const { overallScore, scoreLevel, sectionScores, aiAnalysis, quickWins } = scoreData;

    switch (sectionKey) {
      case "overview":
        return (
          <div className="section-content">
            {/* Overview Header */}
            <Card className="overview-header-card">
              <div>
                <Title level={2} style={{ marginBottom: 8 }}>
                  Your 5-Second Resume Score
                </Title>
                <Paragraph style={{ marginBottom: 16, fontSize: 15, color: "#666" }}>
                  {aiAnalysis?.fiveSecondTest?.firstImpression}
                </Paragraph>

                {/* Compact Sub-Scores */}
                <Row gutter={12} style={{ marginBottom: 16 }}>
                  <Col span={8}>
                    <div className="compact-stat">
                      <div className="stat-value">
                        <RobotOutlined style={{ marginRight: 4, color: "#1890ff" }} />
                        {aiAnalysis?.atsScore || 0}/100
                      </div>
                      <div className="stat-title">ATS Score</div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className="compact-stat">
                      <div className="stat-value">
                        <ReadOutlined style={{ marginRight: 4, color: "#1890ff" }} />
                        {aiAnalysis?.readabilityScore || 0}/100
                      </div>
                      <div className="stat-title">Readability</div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className="compact-stat">
                      <div className="stat-value">
                        <ThunderboltOutlined style={{ marginRight: 4, color: "#1890ff" }} />
                        {aiAnalysis?.impactScore || 0}/100
                      </div>
                      <div className="stat-title">Impact Score</div>
                    </div>
                  </Col>
                </Row>

                {/* Hiring Manager Perspective */}
                <Alert
                  message="Hiring Manager's Perspective"
                  description={aiAnalysis?.fiveSecondTest?.hiringManagerPerspective}
                  type={
                    aiAnalysis?.fiveSecondTest?.recommendation?.includes("PASS")
                      ? "success"
                      : aiAnalysis?.fiveSecondTest?.recommendation?.includes("MAYBE")
                      ? "warning"
                      : "error"
                  }
                  showIcon
                  style={{ marginBottom: 24 }}
                />

                {/* Summary and CTA inside the same card */}
                <div
                  className="cta-section"
                  style={{
                    background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                    borderRadius: "16px",
                    padding: "40px",
                    textAlign: "center",
                    color: "#333",
                    marginTop: "24px",
                    border: "1px solid #dee2e6",
                  }}
                >
                  <Paragraph style={{ fontSize: 16, marginBottom: 24, color: "#555" }}>
                    Your resume shows strong potential but lacks the impact and detail that recruiters and hiring
                    managers look for in the first 5 seconds. With AI-powered enhancement, you can add quantified
                    achievements, organize your skills, and tailor your summary to stand out for full stack
                    developer roles. Let AI help you optimize your resume for both ATS and human reviewers—boosting
                    your chances of landing interviews.
                  </Paragraph>

                  <Button
                    type="primary"
                    size="large"
                    icon={<RocketOutlined />}
                    onClick={handleEnhanceClick}
                    style={{
                      height: 48,
                      fontSize: 16,
                      fontWeight: 600,
                      paddingLeft: 24,
                      paddingRight: 24,
                      background: "#1890ff",
                      color: "white",
                      border: "none",
                    }}
                  >
                    Enhance My Resume with AI
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );

      case "sections":
        return (
          <div className="section-content">
            <Card>
              <Title level={3} style={{ marginBottom: 24 }}>
                <BarChartOutlined style={{ marginRight: 8, color: "#1890ff" }} />
                Detailed Section Analysis
              </Title>
              <Row gutter={[24, 16]}>
                {Object.entries(sectionScores || {}).map(([key, data]) => (
                  <Col xs={24} md={12} lg={8} key={key} style={{ marginBottom: "2px" }}>
                    <SectionScoreCard
                      title={key.charAt(0).toUpperCase() + key.slice(1)}
                      score={data.score}
                      whatsGood={data.whatsGood}
                      whatToImprove={data.whatToImprove}
                      suggestions={data.suggestions}
                      icon={<FileTextOutlined />}
                    />
                  </Col>
                ))}
              </Row>
            </Card>
          </div>
        );

      case "strengths":
        return (
          <div className="section-content">
            <Card className="strengths-improvements-card">
              <Title level={3} style={{ marginBottom: 32 }}>
                <CheckCircleOutlined style={{ marginRight: 8, color: "#52c41a" }} />
                Strengths & Improvements
              </Title>
              <Row gutter={[32, 24]} align="top">
                <Col xs={24} md={12}>
                  <div className="strengths-section">
                    <Title level={4} style={{ marginBottom: 16, color: "#52c41a" }}>
                      <CheckCircleOutlined style={{ marginRight: 8 }} />
                      Key Strengths
                    </Title>
                    <div className="strengths-list">
                      {aiAnalysis?.fiveSecondTest?.keyStrengths?.map((item, i) => (
                        <div
                          key={i}
                          className="strength-item"
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginBottom: "12px",
                            padding: "8px 0",
                          }}
                        >
                          <CheckCircleOutlined
                            style={{
                              marginRight: 12,
                              color: "#52c41a",
                              fontSize: "16px",
                              marginTop: "2px",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: "14px",
                              lineHeight: "1.5",
                              color: "#333",
                            }}
                          >
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <div className="improvements-section">
                    <Title level={4} style={{ marginBottom: 16, color: "#ff4d4f" }}>
                      <ExclamationCircleOutlined style={{ marginRight: 8 }} />
                      Needs Improvement
                    </Title>
                    <div className="improvements-list">
                      {aiAnalysis?.fiveSecondTest?.immediateWeaknesses?.map((item, i) => (
                        <div
                          key={i}
                          className="improvement-item"
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginBottom: "12px",
                            padding: "8px 0",
                          }}
                        >
                          <CloseCircleOutlined
                            style={{
                              marginRight: 12,
                              color: "#ff4d4f",
                              fontSize: "16px",
                              marginTop: "2px",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: "14px",
                              lineHeight: "1.5",
                              color: "#333",
                            }}
                          >
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </div>
        );

      case "quickwins":
        return (
          <div className="section-content">
            <Card>
              <Title level={3} style={{ marginBottom: 24 }}>
                <RocketOutlined style={{ marginRight: 8, color: "#722ed1" }} />
                Quick Wins to Boost Your Score
              </Title>
              <Row gutter={[16, 16]} align="stretch">
                {quickWins?.map((quickWin, index) => (
                  <Col xs={24} md={12} lg={8} key={index} style={{ display: "flex" }}>
                    <QuickWinCard quickWin={quickWin} index={index} />
                  </Col>
                ))}
              </Row>
            </Card>
          </div>
        );

      case "industry":
        return (
          <div className="section-content">
            <Card className="industry-fit-card">
              <Title level={3} style={{ marginBottom: 32 }}>
                <BulbOutlined style={{ marginRight: 8, color: "#faad14" }} />
                Industry Fit Analysis
              </Title>
              <Row gutter={[32, 24]} align="top">
                <Col xs={24} md={8}>
                  <div className="industry-section">
                    <Title level={5} style={{ marginBottom: 16, color: "#333" }}>
                      Detected Industry
                    </Title>
                    <div
                      className="industry-value"
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#333",
                        marginBottom: "16px",
                        lineHeight: "1.4",
                      }}
                    >
                      {aiAnalysis?.industryFit?.detected}
                    </div>
                    <Progress
                      percent={aiAnalysis?.industryFit?.relevance}
                      strokeColor={{
                        "0%": "#1890ff",
                        "100%": "#52c41a",
                      }}
                      strokeWidth={8}
                      showInfo={true}
                      format={(percent) => `${percent}%`}
                      style={{ marginBottom: "8px" }}
                    />
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="keywords-section">
                    <Title level={5} style={{ marginBottom: 16, color: "#333" }}>
                      Missing Keywords
                    </Title>
                    <div
                      className="keyword-tags"
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        alignItems: "flex-start",
                      }}
                    >
                      {aiAnalysis?.industryFit?.missingKeywords?.map((keyword, i) => (
                        <Tag
                          key={i}
                          color="#faad14"
                          style={{
                            borderRadius: "16px",
                            padding: "4px 12px",
                            fontSize: "13px",
                            fontWeight: "500",
                            border: "none",
                            marginBottom: "4px",
                          }}
                        >
                          {keyword}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="suggestions-section">
                    <Title level={5} style={{ marginBottom: 16, color: "#333" }}>
                      Suggestions
                    </Title>
                    <div className="suggestions-list">
                      {aiAnalysis?.industryFit?.suggestions?.map((item, i) => (
                        <div
                          key={i}
                          className="suggestion-item"
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginBottom: "12px",
                            padding: "8px 0",
                          }}
                        >
                          <BulbOutlined
                            style={{
                              marginRight: 12,
                              color: "#1890ff",
                              fontSize: "16px",
                              marginTop: "2px",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontSize: "14px",
                              lineHeight: "1.5",
                              color: "#555",
                            }}
                          >
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </div>
        );

      default:
        return (
          <div className="section-content">
            <Card>
              <Title level={3}>Section: {sectionKey}</Title>
              <Paragraph>Content for {sectionKey} section would appear here.</Paragraph>
              <pre>{JSON.stringify({ sectionKey, hasData: !!scoreData }, null, 2)}</pre>
            </Card>
          </div>
        );
    }
  };

  // Render loading state while checking plan
  if (checkingPlan) {
    return (
      <AppLayout>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  // Render loading state during upload
  if (loading) {
    return (
      <AppLayout>
        <div style={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "100vh",
          overflowY: "auto",
          width: "100%"
        }}>
          <LoadingAnimation stage={loadingStage} />
        </div>
      </AppLayout>
    );
  }

  // Render score results
  if (scoreData) {
    const { overallScore, scoreLevel, sectionScores, aiAnalysis, quickWins, enhancementCTA } = scoreData;

    const navigationItems = getNavigationItems(scoreData);

    return (
      <AppLayout>
        <div className="five-second-score-page results-view sidebar-layout">
          <div className="sidebar-layout-container">
            {/* Left Sidebar Navigation */}
            <div className="sidebar-navigation">
              {/* Score Circle in Sidebar */}
              <div className="sidebar-score">
                <div className="sidebar-score-circle">
                  <ScoreSvg score={overallScore / 100} scoreText={`${overallScore}/100`} />
                </div>
              </div>

              <div className="sidebar-header">
                <Title level={4} style={{ margin: "16px 16px 8px 16px", color: "#333" }}>
                  Resume Analysis
                </Title>
                <Text style={{ margin: "0 16px", color: "#666", fontSize: 13 }}>
                  Navigate through your detailed analysis
                </Text>
              </div>

              <div className="sidebar-menu">
                {navigationItems.map((item) => (
                  <div
                    key={item.key}
                    className={`menu-item ${activeSection === item.key ? "active" : ""} ${
                      item.disabled ? "disabled" : ""
                    }`}
                    onClick={() => !item.disabled && setActiveSection(item.key)}
                  >
                    <div className="menu-item-icon">{item.icon}</div>
                    <div className="menu-item-content">
                      <div className="menu-item-label">{item.label}</div>
                      <div className="menu-item-description">{item.description}</div>
                    </div>
                    {activeSection === item.key && <div className="menu-item-indicator" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Main Content Area */}
            <div className="main-content-area">{renderActiveSection(activeSection, scoreData)}</div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Render upload form (same style as ResumeUpload.jsx)
  return (
    <AppLayout>
      <div style={{
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100vh",
        overflowY: "auto",
        width: "100%",
        position: "relative"
      }}>
        {/* Header with back button */}
        <div style={{
          position: "absolute",
          top: "20px",
          left: "80px",
          zIndex: 10
        }}>
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{
              fontSize: "14px",
              borderColor: "#1890ff",
              color: "#1890ff",
              backgroundColor: "transparent",
              height: "36px",
              paddingLeft: "16px",
              paddingRight: "16px",
              borderRadius: "6px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.3s ease"
            }}
          >
            Back to options
          </Button>
        </div>

        <div style={{ marginTop: "60px", marginBottom: "48px", textAlign: "center", width: "100%" }}>
          <Title level={2}>5-Second Resume Score</Title>
          <Text type="secondary">Upload your resume for instant AI-powered scoring and analysis</Text>
        </div>

        <div style={{ maxWidth: "500px", width: "100%" }}>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {error && (
              <Alert
                message="Error"
                description={error}
                type="error"
                closable
                onClose={() => setError("")}
              />
            )}

            <Card style={{ textAlign: "center" }}>
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Title level={4}>Upload your resume</Title>
                <Text type="secondary">Supported formats: PDF, DOCX (Max size: 5MB)</Text>

                <Dragger
                  accept=".pdf,.docx"
                  beforeUpload={() => false}
                  onChange={(info) => {
                    const { fileList } = info;
                    if (fileList.length > 0) {
                      const newFile = fileList[0].originFileObj;
                      handleDropZoneDrop([], [newFile]);
                    }
                  }}
                  fileList={[]}
                  showUploadList={false}
                  style={{ padding: "20px" }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Add file</p>
                  <p className="ant-upload-hint">or drop file to upload</p>
                </Dragger>
              </Space>
            </Card>
          </Space>
        </div>
      </div>
    </AppLayout>
  );
};

export default FiveSecondScore;
